import { z } from "zod/v4";

export const vizViewTypeSchema = z.enum([
  "time-series-line",
  "time-series-grouped-line",
  "time-series-area",
  "time-series-composed",
  "scatter-plot",
  "bar-ranking",
  "bar-comparison",
  "table",
]);

const vizFieldLabelDomainSchema = z.enum(["ua.defaultChannelGroup"]);
const vizLineInterpolationSchema = z.enum(["linear", "monotoneX"]);
const vizFieldComparisonRoleSchema = z.enum(["current", "previous"]);

const vizMetricAggregationSchema = z.discriminatedUnion("kind", [
  z
    .object({
      kind: z.literal("additive"),
    })
    .strict(),
  z
    .object({
      kind: z.literal("ratio"),
      numeratorKey: z.string().min(1),
      denominatorKey: z.string().min(1),
      offset: z.number().finite().optional(),
      zeroDenominatorValue: z.union([z.number().finite(), z.null()]).optional(),
    })
    .strict(),
  z
    .object({
      kind: z.literal("non-aggregatable"),
      reason: z.enum(["rank", "duration", "other"]),
    })
    .strict(),
]);

const vizFieldSchema = z.object({
  name: z.string().min(1),
  role: z.enum(["x", "y", "series", "dim", "metric"]),
  type: z.enum(["number", "string", "date"]),
  label: z.string().min(1).optional(),
  aliases: z.array(z.string().min(1)).optional(),
  unit: z.string().optional(),
  format: z
    .enum(["number", "percent", "duration", "date", "string"])
    .optional(),
  sortable: z.boolean().optional(),
  filterable: z.boolean().optional(),
  exportable: z.boolean().optional(),
  defaultVisible: z.boolean().optional(),
  internal: z.boolean().optional(),
  labelDomain: vizFieldLabelDomainSchema.optional(),
  aggregation: vizMetricAggregationSchema.optional(),
  lineInterpolation: vizLineInterpolationSchema.optional(),
  metricFamily: z.string().min(1).optional(),
  movingAverageWindowDays: z.number().int().positive().optional(),
  comparisonRole: vizFieldComparisonRoleSchema.optional(),
});

/**
 * dataset 本体は汎用 row 構造で保持する。
 *
 * @remarks
 * 行の key 制約は row 側ではなく `fields` 側を正本として扱う。
 * compile / validation 層では `fields` を参照して利用可能キーを検証する。
 */
const vizDatasetRowSchema = z.record(
  z.string(),
  z.union([z.string(), z.number(), z.null()]),
);

const vizDatasetContextSchema = z
  .object({
    queryContext: z.record(z.string(), z.unknown()).optional(),
  })
  .strict();

/**
 * dataset の意味論メタデータ。
 *
 * @remarks
 * `kind` ではなく family/variant/compare/view で表現し、
 * 同一 row 形式のまま view 適性を判断できるようにする。
 */
const vizDatasetProfileSchema = z
  .object({
    datasetFamily: z.string().min(1),
    variant: z.string().min(1),
    compareMode: z.enum(["none", "period", "group"]),
    supportedViews: z.array(vizViewTypeSchema).min(1),
  })
  .strict();

const vizDatasetDefaultsSchema = z
  .object({
    x: z.string().optional(),
    y: z.array(z.string()).optional(),
    series: z.string().optional(),
    category: z.string().optional(),
    value: z.string().optional(),
  })
  .optional();

const vizDatasetSchema = z.object({
  id: z.string().min(1),
  baseId: z.string().min(1).optional(),
  profile: vizDatasetProfileSchema,
  fields: z.array(vizFieldSchema).min(1),
  rows: z.array(vizDatasetRowSchema),
  defaults: vizDatasetDefaultsSchema,
  source: z.object({
    toolName: z.string().min(1),
    toolCallId: z.string().min(1),
  }),
  context: vizDatasetContextSchema.optional(),
  notes: z.string().optional(),
});

const vizDatasetOutputSchema = z.object({
  primaryDatasetId: z.string().min(1),
  datasets: z.record(z.string(), vizDatasetSchema),
});

export type VizMetricAggregation = z.infer<typeof vizMetricAggregationSchema>;
export type VizField = z.infer<typeof vizFieldSchema>;
export type VizFieldLabelDomain = z.infer<typeof vizFieldLabelDomainSchema>;
export type VizViewType = z.infer<typeof vizViewTypeSchema>;
export type VizDatasetRow = z.infer<typeof vizDatasetRowSchema>;
export type VizDataset = z.infer<typeof vizDatasetSchema>;
export type VizDatasetOutput = z.infer<typeof vizDatasetOutputSchema>;
